











































































































































import { Vue, Component, Prop, Watch } from 'vue-property-decorator'
import {
  ISnackbar,
  Box,
  MachineSettings,
  ProgramSettings,
  RobotTypeEnum,
  ConveyorConfigurationEnum, Settings
} from '@/model'
import { Conveyor3D } from '@/libs/conveyor3D'
import { defaultConveyorInfeedCollection, robots } from '@/api/const'
import { isBoxLongSideLeadingOnly } from '@/libs/pattern/boxUtils'
import { mapGetters } from 'vuex'
import LabelPositionEnum = ProgramSettings.LabelPositionEnum

@Component({
  computed: {
    ...mapGetters('machinesettings', ['fixedMachineSettings']),
    ...mapGetters('settings', ['settings'])
  }
})
export default class SimpleFormC extends Vue {
  @Prop({ required: true }) private value!: ProgramSettings
  @Prop({ required: true }) private parentBus!: Vue
  @Prop({ required: true }) private patternHasBoxPositions!: boolean
  @Prop({ required: true }) private machineSettings!: MachineSettings
  @Prop({ required: true }) private box!: Box

  private valid: boolean = false
  private rules: object = {
    required: (v: any) => !!v || this.localText('general.required'),
    interval: (v: any) => (v === 0 || (v && v >= -360 && v <= 360)) || this.localText('angleLimit'),
  }

  private programSettings: ProgramSettings = Object.assign({}, this.value)
  private rotation: number = 0
  private conveyorConfiguration: any = ''
  private boxOrientationOnConveyor: any = ''
  private labelPosition: any = ''
  private approachAngle = 45
  // label position and box orientation shall not be editable if there are layer patterns with box positions
  private labelPositionAndBoxOrientationReadOnly: boolean = this.patternHasBoxPositions

  private conveyorInfeedCollection: any[] = [defaultConveyorInfeedCollection [0]] // default left only

  private labelPositionsCollection: any[] = [
    { id: 1, text: 'robotStep.front', value: 'FRONT', state: false },
    { id: 3, text: 'robotStep.right', value: 'RIGHT', state: false },
    { id: 2, text: 'robotStep.rightFront', value: 'FRONT_RIGHT_CORNER', state: false },
    { id: 4, text: 'robotStep.rightBack', value: 'RIGHT_BACK_CORNER', state: false },
    { id: 5, text: 'robotStep.back', value: 'BACK', state: false },
    { id: 7, text: 'robotStep.left', value: 'LEFT', state: false },
    { id: 8, text: 'robotStep.leftFront', value: 'LEFT_FRONT_CORNER', state: false },
    { id: 6, text: 'robotStep.leftBack', value: 'BACK_LEFT_CORNER', state: false },
  ]
  private boxOrientationsCollection: any[] = [
    { id: 1, text: 'robotStep.shortLeading', value: 'SHORT_SIDE_LEADING' },
    { id: 2, text: 'robotStep.longLeading', value: 'LONG_SIDE_LEADING' }
  ]

  private view!: Conveyor3D
  private isReady: boolean = false
  private showAdvanced: boolean = false
  private fixedMachineSettings!: boolean
  private settings!: Settings
  private isIgnoredPosition: boolean = false
  private initialLabelPosition!: LabelPositionEnum
  private snackbar: ISnackbar = { show: false, text: '', color: '', timeout: 10000 }

  private mounted () {
    this.setInfeedCollection()
    this.populateConveyor()
    this.populateBoxOrientation()
    this.populateLabelPos()
    this.parentBus.$on('stepper_next', this.next)
    this.parentBus.$on('stepper_previous', this.previous)

    const angle = this.programSettings.approachAngle
    this.approachAngle = angle === 0 || angle ? Number(angle) : 45

    this.isIgnoredPosition = this.programSettings.labelPosition === LabelPositionEnum.IGNORE
    this.initialLabelPosition = this.programSettings.labelPosition!
    this.checkLabelPosition()
    const afterCreated = () => {
      // console.log('[DEBUG] afterCreated', this.conveyorConfiguration, this.labelPosition)
      this.view.setCustom(this.isCustomSolution())
      const labelPos = this.labelPosition
      this.moveScene()
      this.view.setBoxOrient(this.boxOrientationOnConveyor)
      this.view.setPosition(this.conveyorConfiguration)
      this.view.setLabel(labelPos)
      this.isReady = true
    }

    // 370 is stepper 'slid-in' transition time
    // so, we want to show 3d element after that
    setTimeout(() => {
      this.view = new Conveyor3D('conveyor3d', ConveyorConfigurationEnum.LEFT, afterCreated)
    }, 1)
  }

  private setInfeedCollection () {
    const robot = robots.find(p => p.id === this.programSettings.robotType)
    if (robot) {
      let infeeds = robot.infeeds
      if (this.isRI20WithCustomConveyorConfig(robot.id)) {
        infeeds = this.machineSettings.customConveyorConfig
      }
      this.conveyorInfeedCollection = defaultConveyorInfeedCollection.filter(p => infeeds.includes(p.id))
    }
  }

  private isRI20WithCustomConveyorConfig(robotType: RobotTypeEnum) {
    return robotType === RobotTypeEnum.RI20_FANUC
        && this.machineSettings.robotType === RobotTypeEnum.RI20_FANUC
        && (this.machineSettings.customConveyorConfig && this.machineSettings.customConveyorConfig.length > 0)
  }

  private isCustomSolution () {
    return (this.fixedMachineSettings && this.machineSettings.robotType === RobotTypeEnum.CUSTOM_SOLUTION)
        || (!this.fixedMachineSettings && this.programSettings.robotType === RobotTypeEnum.CUSTOM_SOLUTION)
  }

  private beforeDestroy () {
    this.parentBus.$off('stepper_next', this.next)
    this.parentBus.$off('stepper_previous', this.previous)
  }

  private next () {
    this.programSettings.boxOrientationOnConveyor = this.boxOrientationOnConveyor
    this.programSettings.labelPosition = this.labelPosition ? this.labelPosition : this.initialLabelPosition
    if (!this.fixedMachineSettings) {
      this.programSettings.conveyorConfiguration = this.conveyorConfiguration
      this.programSettings.approachAngle = this.approachAngle
    }
    if (!this.isCustomSolution()) {
      this.programSettings.ninetyDegreePicking = this.programSettings.ninetyDegreePicking || false
    }
    this.$emit('next', this.programSettings)
  }

  private previous () {
    this.$emit('previous')
  }

  private changeConveyorView (value: ConveyorConfigurationEnum) {
    // console.log('[DEBUG] Select conveyor infeed: change', value)
    this.conveyorConfiguration = value
    this.view.setPosition(value)
  }

  private changeBoxOrientation (value: ProgramSettings.BoxOrientationOnConveyorEnum) {
    this.boxOrientationOnConveyor = value
    this.view.setBoxOrient(value)
  }

  private changeLabelPosition (value: ProgramSettings.LabelPositionEnum) {
    this.labelPosition = value
    this.view.setLabel(value)
  }

  private snackBarHandler (show: boolean, color: string, text: string) {
    this.snackbar.show = show
    this.snackbar.color = color
    this.snackbar.text = text
  }

  private ignoreLabelPositionChanged() {
    this.labelPosition = this.isIgnoredPosition ? LabelPositionEnum.IGNORE : null
        // (this.initialLabelPosition === LabelPositionEnum.IGNORE ? null : this.initialLabelPosition)
    if (!this.isIgnoredPosition) {
      if (this.initialLabelPosition !== LabelPositionEnum.IGNORE) {
        this.labelPosition = this.initialLabelPosition
      } else {
        this.labelPosition = LabelPositionEnum.FRONT
        if (this.isLabelPositionInvalid()) {
          this.snackBarHandler(true, 'info', String(this.$t('robotStep.incorrectLabelPosition')))
        }
      }
    }
  }

  // Watcher to emit valid state to "next" button in stepper
  @Watch('valid')
  private validWatcher (value: boolean) {
    // console.log('[DEBUG] SimpleFormC emitting valid', !!value)
    this.$emit('validate', value)
  }

  @Watch('conveyorConfiguration')
  private conveyorConfigWatcher (value: string) {
    if (!this.isReady) {
      return
    }
    this.$emit('conveyorHandler')
    if (!this.isCustomSolution()) {
      this.moveScene()
    }
    const orientation = this.boxOrientationOnConveyor
    if (orientation) {
      this.setBoxLeadingOrientation(orientation)
    }
  }

  @Watch('boxOrientationOnConveyor', { deep: true })
  private boxOrientationOnConveyorWatcher (value: string, oldvalue: string) {
    if (!this.isReady) {
      return
    }
    this.$emit('boxLeadingHandler', value)
    this.setBoxLeadingOrientation(value)
  }

  @Watch('labelPosition', { deep: true })
  private labelPositionWatcher (value: string) {
    if (!this.isReady) {
      return
    }
    this.initialLabelPosition = this.labelPosition === LabelPositionEnum.IGNORE ? this.initialLabelPosition : this.labelPosition
    this.$emit('labelHandler', value)
    if (this.isCustomSolution() && value) {
      this.view.setLabel(value as ProgramSettings.LabelPositionEnum)
    } else if (!this.isCustomSolution()) {
      this.moveScene()
    }
  }

  private populateConveyor () {
    const v = this.programSettings.conveyorConfiguration
    const index = this.conveyorInfeedCollection.findIndex(c => c.value === v)
    if (index > -1) {
      this.conveyorConfiguration = v
    }
  }

  private isBoxLongSideLeadingOnly() {
    return isBoxLongSideLeadingOnly(this.box.length!, this.machineSettings)
  }

  private populateBoxOrientation () {
    const v = this.programSettings.boxOrientationOnConveyor
    const index = this.boxOrientationsCollection.findIndex(b => b.value === v)
    if (index > -1 && this.labelPositionAndBoxOrientationReadOnly) {
      this.boxOrientationOnConveyor = v
    } else if (isBoxLongSideLeadingOnly(this.box.length!, this.machineSettings)) {
      this.boxOrientationOnConveyor = 'LONG_SIDE_LEADING'
    } else {
      this.boxOrientationOnConveyor = 'SHORT_SIDE_LEADING'
    }
  }

  private populateLabelPos () {
    const v = this.programSettings.labelPosition
    const index = this.labelPositionsCollection.findIndex(l => l.value === v)
    if (index > -1) {
      this.labelPosition = v
    }
  }

  private setBoxLeadingOrientation (value: string) {
    if (value === 'SHORT_SIDE_LEADING' || value === '') {
      if (this.conveyorConfiguration === 'LEFT' || this.conveyorConfiguration === 'RIGHT') {
        this.rotation = 0
      } else {
        this.rotation = 90
      }
    } else if (value === 'LONG_SIDE_LEADING') {
      if (this.conveyorConfiguration === 'LEFT' || this.conveyorConfiguration === 'RIGHT') {
        this.rotation = 90
      } else {
        this.rotation = 0
      }
    }
  }

  private moveScene () {
    this.view.moveScene(this.conveyorConfiguration, this.isCustomSolution() ? LabelPositionEnum.FRONT : this.labelPosition)
  }

  private viewClick () {
    // this.view.rotate()
  }

  private localText (b: string): string {
    return String(this.$t(b))
  }

  private isLabelPositionInvalid(): boolean {
    return (!this.isDbEnabled() && this.initialLabelPosition === LabelPositionEnum.IGNORE)
        || (!this.labelPosition && this.initialLabelPosition !== LabelPositionEnum.IGNORE)
  }

  private checkLabelPosition() {
    if (this.isLabelPositionInvalid()) {
      this.labelPosition = LabelPositionEnum.FRONT
      this.snackBarHandler(true, 'info', String(this.$t('robotStep.incorrectLabelPosition')))
    } else {
      this.snackBarHandler(false, '', '')
    }
  }

  private closeSnackbar () {
    this.snackbar.show = false
  }

  private isDbEnabled() {
    return this.settings.dbParams && this.settings.dbParams.enabled && this.settings.dbParams.host != null
  }
}
